import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { gql, useMutation } from '@apollo/client';
import { Box, Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { UploadFile, VisibilityOutlined } from '@mui/icons-material';
import StyledTextField from 'common/theme/fields/StyledTextfield';
import PdfViewModal from 'components/shared/modals/PdfViewModal';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';

const MAX_SIZE = 15 * 1024 * 1024; // 15MB

const UPLOAD_INVOICE = gql`
  mutation UploadTrainerPaymentInvoice($id: ID!, $invoice: Upload!) {
    uploadTrainerPaymentInvoice(id: $id, invoice: $invoice) {
      id
      invoice_url
    }
  }
`;

/**
 * Campo de FACTURA (admin, modo edición): muestra la factura actual embebida ("Ver") y permite
 * SUBIR un PDF propio (≤15MB) que se almacena en el backend y actualiza `invoice_url`.
 * Se usa dentro de un <Field name="invoice_url"> para reflejar la URL en el formulario.
 */
function InvoiceUploadField({ input, paymentId = null }) {
  const { t } = useTranslation(['labels', 'buttons', 'errors']);
  const dispatch = useDispatch();
  const fileRef = useRef(null);
  const [viewerOpen, setViewerOpen] = useState(false);

  const url = typeof input.value === 'string' ? input.value : '';
  const filename = url ? url.split('/').at(-1) : '';

  const [uploadInvoice, { loading }] = useMutation(UPLOAD_INVOICE, {
    onCompleted: (data) => {
      input.onChange(data?.uploadTrainerPaymentInvoice?.invoice_url || '');
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({
        key: 'invoice-upload-ok', variant: 'success',
        message: t('invoiceUploaded', { ns: 'labels', defaultValue: 'Factura cargada correctamente' })
      })));
    },
    onError: () => {
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({
        key: 'invoice-upload-error', variant: 'error',
        message: t('invoiceUploadError', { ns: 'errors', defaultValue: 'No se pudo cargar la factura' })
      })));
    }
  });

  const onPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') {
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: 'invoice-pdf', variant: 'error', message: t('onlyPdf', { ns: 'errors', defaultValue: 'Solo se permiten archivos PDF' }) })));
      return;
    }
    if (file.size > MAX_SIZE) {
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: 'invoice-size', variant: 'error', message: t('upload.size.file', { ns: 'errors' }) })));
      return;
    }
    uploadInvoice({ variables: { id: paymentId, invoice: file } });
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={onPick} />
        <StyledTextField
          label={t('invoice', { ns: 'labels' })}
          value={filename}
          placeholder={t('noInvoiceYet', { ns: 'labels', defaultValue: 'Sin factura cargada' })}
          InputProps={{ readOnly: true }}
          sx={{ flexGrow: 1, marginRight: (theme) => theme.spacing(1) }}
        />
        {url && (
          <Tooltip title={t('view', { ns: 'buttons', defaultValue: 'Ver' })}>
            <IconButton onClick={() => setViewerOpen(true)} sx={{ height: '47px', color: 'gold.main', '&:hover': { backgroundColor: 'gold.soft' } }}>
              <VisibilityOutlined />
            </IconButton>
          </Tooltip>
        )}
        {paymentId && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <UploadFile />}
            disabled={loading}
            onClick={() => fileRef.current?.click()}
            sx={{ height: '47px', whiteSpace: 'nowrap' }}
          >
            {t('upload', { ns: 'buttons' })}
          </Button>
        )}
      </Box>
      <PdfViewModal open={viewerOpen} src={url} title={filename} filename={filename} onClose={() => setViewerOpen(false)} />
    </>
  );
}

InvoiceUploadField.propTypes = {
  input: PropTypes.object.isRequired,
  paymentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default InvoiceUploadField;
